import { formatNumber, formatPrice } from '../lib/format.js';
import type { PublicPlan } from '../lib/signup-api.js';

interface PlanPickerProps {
  plans: PublicPlan[];
  onSelect: (plan: PublicPlan) => void;
}

// Plans stack vertically — the picker lives inside the narrow sign-in-style
// card of the BrandShell, not a full-width marketing page.
export function PlanPicker({ plans, onSelect }: PlanPickerProps) {
  if (plans.length === 0) {
    return <p className="text-center text-gray-500 py-12">No plans available at this time.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {plans.map((plan) => (
        <div key={plan.id} className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-gray-900">{plan.name}</h2>
            <p className="text-xl font-bold text-gray-900">
              {formatPrice(plan.unitAmount, plan.currency)}
              <span className="text-sm font-normal text-gray-500">/{plan.interval}</span>
            </p>
          </div>

          <ul className="text-sm text-gray-600 space-y-1">
            <li>{formatNumber(plan.includedTextSegments)} SMS segments</li>
            <li>{formatNumber(plan.includedAiCredits)} AI credits</li>
            {plan.brandedSubdomain && <li>Branded web address (yourclub.groupfire.com)</li>}
            {plan.description && <li className="pt-1 text-gray-500">{plan.description}</li>}
          </ul>

          <button
            type="button"
            onClick={() => onSelect(plan)}
            className="w-full rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            Choose {plan.name}
          </button>
        </div>
      ))}

      <section
        aria-labelledby="usage-explainer-heading"
        className="min-w-0 break-words space-y-3 text-sm text-gray-600"
      >
        <h2 id="usage-explainer-heading" className="font-semibold text-gray-900">
          What are SMS segments and AI credits?
        </h2>
        <p>
          <strong>SMS segment:</strong> the unit carriers bill a text by. A plain-text message up to
          160 characters is 1 segment; longer messages split into 153-character segments. Messages
          with emoji (some emoji count as 2) or special characters: 70 characters for 1 segment, 67
          each when split. A 200-character plain message uses 2 segments.
        </p>
        <p>
          <strong>AI credit:</strong> 1 AI credit covers about $0.01 of AI provider cost. Each AI
          request needs at least 1 credit available; usage is billed in whole credits, rounded up.
        </p>
        <p>
          <strong>Renewal:</strong> Included SMS segments and AI credits are per billing period and
          reset when your subscription renews (monthly on monthly plans). Unused included amounts do
          not roll over. Credits bought separately as top-ups are kept and are not reset.
        </p>
        <p>
          <strong>At the limit:</strong> Usage draws from your included amount first, then any
          purchased top-up balance. After that, if your plan has overage pricing and your account
          has a card on file in good standing, additional use continues and is billed as overage.
          Otherwise, texting and AI features stop working (messages aren't sent) until your
          allowance renews or you add more. Top-up and overage prices are plan-specific and shown in
          billing settings.
        </p>
      </section>
    </div>
  );
}
